import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Droplets, Clock, CheckCircle, Sparkles, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function HydrationGuide() {
  const navigate = useNavigate();

  const benefits = [
    { title: "30% More Energy", desc: "Even mild dehydration causes fatigue" },
    { title: "Clearer Skin", desc: "Hydration plumps skin and reduces wrinkles" },
    { title: "Better Digestion", desc: "Water is essential for nutrient absorption" },
    { title: "Fewer Headaches", desc: "Dehydration is a leading cause of headaches" },
    { title: "Improved Focus", desc: "Your brain is 75% water" },
    { title: "Natural Detox", desc: "Helps kidneys flush toxins efficiently" }
  ];

  const flavorIdeas = [
    { combo: "Lemon + Mint", desc: "Classic and refreshing" },
    { combo: "Cucumber + Lime", desc: "Spa-like and cooling" },
    { combo: "Strawberry + Basil", desc: "Sweet with an herbal twist" },
    { combo: "Orange + Ginger", desc: "Zesty and energizing" },
    { combo: "Watermelon + Rosemary", desc: "Unexpected and delicious" },
    { combo: "Blueberry + Lavender", desc: "Calming and antioxidant-rich" }
  ];

  const schedule = [
    { time: "7 AM", action: "Wake up with 16oz water", tip: "Before coffee, drink water to rehydrate after sleep" },
    { time: "9 AM", action: "8oz with breakfast", tip: "Helps digestion and energy" },
    { time: "11 AM", action: "8oz mid-morning", tip: "Set a phone reminder" },
    { time: "1 PM", action: "8oz with lunch", tip: "Avoid drinking too much during meals" },
    { time: "3 PM", action: "8oz afternoon pick-me-up", tip: "Replace that afternoon coffee with water" },
    { time: "5 PM", action: "8oz before dinner", tip: "Can help prevent overeating" },
    { time: "7 PM", action: "8oz with dinner", tip: "Slow down after this to avoid nighttime trips" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm font-semibold mb-4">
            Week 2 • Day 3 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Hydration Challenge
          </h1>
          <p className="text-xl text-secondary-600">Unlock Energy, Clarity, and Radiant Skin</p>
        </div>

        {/* Hero Challenge */}
        <section className="bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Droplets className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Challenge</h3>
              <p className="text-cyan-100 text-lg">
                Drink 64oz (8 cups) of water today. Fill a 32oz bottle and drink it twice. It sounds simple, but most women are chronically dehydrated without realizing it.
              </p>
            </div>
          </div>
        </section>

        {/* Why It Matters */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Why Hydration Matters</h2>
          <p className="text-secondary-700 mb-6">
            Your body is 60% water. Every cell, tissue, and organ needs water to function. Even 1-2% dehydration 
            can impair cognitive function, energy, and mood. Here's what proper hydration does for you:
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            {benefits.map((item, i) => (
              <div key={i} className="text-center p-4 bg-cyan-50 rounded-xl">
                <p className="font-bold text-cyan-700 mb-1">{item.title}</p>
                <p className="text-sm text-secondary-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Hydration Schedule */}
        <section className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-8 border-2 border-cyan-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Clock className="w-6 h-6 text-cyan-500" />
            Sample Hydration Schedule
          </h2>
          <div className="space-y-3">
            {schedule.map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-3 bg-white rounded-lg">
                <span className="font-bold text-cyan-600 w-16 flex-shrink-0">{item.time}</span>
                <div>
                  <p className="font-medium text-secondary-900">{item.action}</p>
                  <p className="text-sm text-secondary-500">{item.tip}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Flavor Ideas */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-amber-500" />
            Infused Water Ideas
          </h2>
          <p className="text-secondary-600 mb-4">If plain water feels boring, try these combinations:</p>
          <div className="grid md:grid-cols-2 gap-4">
            {flavorIdeas.map((idea, index) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl">
                <Droplets className="w-6 h-6 text-cyan-500 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-secondary-900">{idea.combo}</h3>
                  <p className="text-sm text-secondary-600">{idea.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Dehydration Signs */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            Signs You're Dehydrated
          </h2>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              "Dark yellow urine", "Feeling tired or sluggish", "Dry mouth or lips",
              "Headaches", "Difficulty concentrating", "Dizziness when standing",
              "Dry or dull skin", "Constipation", "Feeling hungry when you just ate"
            ].map((sign, index) => (
              <div key={index} className="flex items-center gap-2 text-secondary-700">
                <CheckCircle className="w-4 h-4 text-cyan-500 flex-shrink-0" />
                {sign}
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default HydrationGuide;
